import type { Item, PricePoint } from './types';

/** After this many failed checks in a row the server stops trying (see backend/internal/prices). */
export const MAX_PRICE_CHECK_FAILURES = 5;

type WatchFields = Pick<Item, 'price_watch' | 'price_cents' | 'price_before_cents'>;

/** How much cheaper a watched piece got since watching started, or null. */
export function priceDrop(item: WatchFields): { beforeCents: number; percent: number } | null {
  if (!item.price_watch || !item.price_cents || item.price_before_cents <= item.price_cents) return null;
  const percent = Math.round((1 - item.price_cents / item.price_before_cents) * 100);
  return percent >= 1 ? { beforeCents: item.price_before_cents, percent } : null;
}

/** PocketBase dates look like "2026-10-09 12:00:00.000Z". */
export function parseServerDate(s: string): number {
  return s ? Date.parse(s.replace(' ', 'T')) : NaN;
}

export function timeAgo(iso: string, now = Date.now()): string {
  const t = parseServerDate(iso);
  if (Number.isNaN(t)) return '';
  const min = Math.floor((now - t) / 60_000);
  if (min < 1) return 'gerade eben';
  if (min < 60) return `vor ${min} Min.`;
  const h = Math.floor(min / 60);
  if (h < 24) return `vor ${h} Std.`;
  const d = Math.floor(h / 24);
  return d === 1 ? 'gestern' : `vor ${d} Tagen`;
}

const FAILURE_HINTS: Record<string, string> = {
  blocked: 'Der Shop lässt den Server nicht auf die Seite.',
  unreachable: 'Der Shop war nicht erreichbar.',
  no_price: 'Auf der Seite war kein Preis zu finden.',
  currency: 'Der Shop zeigt den Preis in einer anderen Währung.',
};

/** Why a price check failed, in words; for a code from price_check_error. */
export function priceCheckHint(error: string): string {
  return FAILURE_HINTS[error] ?? 'Die Prüfung hat nicht geklappt.';
}

export interface WatchStatus {
  text: string;
  /** warn: checks fail or stopped; the piece needs a look. */
  tone: 'ok' | 'warn';
}

/** Where watching stands, in one line for the item form; null when not watching. */
export function watchStatus(
  item: Pick<
    Item,
    'price_watch' | 'link' | 'bought' | 'price_checked_at' | 'price_check_failures' | 'price_check_error' | 'sold_out'
  >,
  now = Date.now(),
): WatchStatus | null {
  if (!item.price_watch) return null;
  if (!item.link) return { text: 'Ohne Link zum Shop lässt sich der Preis nicht prüfen.', tone: 'warn' };
  if (item.bought) return { text: 'Gekauft, der Preis wird nicht mehr geprüft.', tone: 'ok' };
  const hint = priceCheckHint(item.price_check_error);
  if (item.price_check_failures >= MAX_PRICE_CHECK_FAILURES)
    return { text: `Wird nicht mehr geprüft. ${hint}`, tone: 'warn' };
  if (item.price_check_failures > 0)
    return { text: `Letzte Prüfung ${timeAgo(item.price_checked_at, now)} ohne Ergebnis. ${hint}`, tone: 'warn' };
  if (!item.price_checked_at) return { text: 'Wird in der nächsten Stunde zum ersten Mal geprüft.', tone: 'ok' };
  const soldOut = item.sold_out ? ' Gerade ausverkauft.' : '';
  return { text: `${sentence(`Zuletzt geprüft ${timeAgo(item.price_checked_at, now)}`)}${soldOut}`, tone: 'ok' };
}

export interface PriceChart {
  /** SVG path of a step line over the whole width. */
  path: string;
  lowCents: number;
  highCents: number;
  firstCents: number;
}

/**
 * A small step chart of the price history: the server stores a point only
 * when the price changed, so each price holds until the next point and the
 * last one until now. Null until there are two prices to compare.
 */
export function priceChart(points: PricePoint[], width: number, height: number, now = Date.now()): PriceChart | null {
  const pts = points
    .map((p) => ({ t: parseServerDate(p.checked_at), c: p.price_cents }))
    .filter((p) => !Number.isNaN(p.t) && p.c > 0)
    .sort((a, b) => a.t - b.t);
  if (pts.length < 2) return null;
  const low = Math.min(...pts.map((p) => p.c));
  const high = Math.max(...pts.map((p) => p.c));
  const t0 = pts[0]!.t;
  const span = Math.max(now, pts[pts.length - 1]!.t) - t0 || 1;
  const pad = 2;
  const x = (t: number) => round(((t - t0) / span) * width);
  const y = (c: number) => round(high === low ? height / 2 : pad + ((high - c) / (high - low)) * (height - 2 * pad));
  let path = `M0 ${y(pts[0]!.c)}`;
  for (const p of pts.slice(1)) path += ` H${x(p.t)} V${y(p.c)}`;
  path += ` H${width}`;
  return { path, lowCents: low, highCents: high, firstCents: pts[0]!.c };
}

/** "vor 2 Std." already ends the sentence. */
const sentence = (s: string) => (s.endsWith('.') ? s : `${s}.`);

const round = (n: number) => Math.round(n * 10) / 10;
